<template>
  <div class="vx-row">
    <vx-card>
      <!-- Filter Section -->
      <div class="mb-4">
        <div class="vx-row">
          <div class="vx-col w-full">
            <h4 class="mb-3">{{ $t('QuickFilters') }}</h4>
            <div class="vx-row">
              <div class="vx-col w-1/4">
                <vs-input
                  v-model="quickFilters.system"
                  :placeholder="$t('System')"
                  icon="computer"
                  @input="applyQuickFilters">
                </vs-input>
              </div>
              <div class="vx-col w-1/4">
                <vs-input
                  v-model="quickFilters.action"
                  :placeholder="$t('Action')"
                  icon="play_arrow"
                  @input="applyQuickFilters">
                </vs-input>
              </div>
              <div class="vx-col w-1/4">
                <vs-input
                  v-model="quickFilters.userEmail"
                  :placeholder="$t('UserEmail')"
                  icon="person"
                  @input="applyQuickFilters">
                </vs-input>
              </div>
              <div class="vx-col w-1/4">
                <vs-select
                  v-model="quickFilters.processed"
                  :placeholder="$t('ProcessedStatus')"
                  @input="applyQuickFilters">
                  <vs-select-item value="" :text="$t('All')" />
                  <vs-select-item value="true" :text="$t('Processed')" />
                  <vs-select-item value="false" :text="$t('Unprocessed')" />
                </vs-select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="mb-4" style="display: flex; gap: 10px; justify-content: space-between;">
        <div style="display: flex; gap: 10px;">
          <vs-button 
            color="primary" 
            type="filled" 
            icon="refresh"
            @click="loadSignals"
            :loading="loading">
            {{ $t('Refresh') }}
          </vs-button>
          
          <vs-button 
            color="success" 
            type="filled" 
            icon="filter_list"
            @click="showFilterModal = true">
            {{ $t('ManageFilters') }}
          </vs-button>
          
          <vs-button 
            color="warning" 
            type="filled" 
            icon="label"
            @click="showTagModal = true">
            {{ $t('ManageTags') }}
          </vs-button>
          
          <vs-button 
            color="dark" 
            type="filled" 
            icon="check"
            @click="showApplyTagsModal = true">
            {{ $t('ApplyTags') }}
          </vs-button>
        </div>
        
        <div>
          <vs-button 
            color="primary" 
            type="border" 
            icon="add"
            @click="showCreateSignalModal = true">
            {{ $t('CreateTestSignal') }}
          </vs-button>
        </div>
      </div>

      <!-- Signals Table -->
      <vs-table :data="signals">
        <template slot="thead">
          <vs-th>ID</vs-th>
          <vs-th>{{ $t('System') }}</vs-th>
          <vs-th>{{ $t('Action') }}</vs-th>
          <vs-th>{{ $t('UserEmail') }}</vs-th>
          <vs-th>{{ $t('Context') }}</vs-th>
          <vs-th>{{ $t('SOPHint') }}</vs-th>
          <vs-th>{{ $t('Processed') }}</vs-th>
          <vs-th>{{ $t('Created') }}</vs-th>
          <vs-th>{{ $t('Tags') }}</vs-th>
          <vs-th>{{ $t('Actions') }}</vs-th>
        </template>
        <template slot-scope="{ data }">
          <tbody>
          <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
            <vs-td>{{ tr.Id }}</vs-td>
            <vs-td>
              <vs-chip color="primary">{{ tr.System }}</vs-chip>
            </vs-td>
            <vs-td>{{ tr.Action }}</vs-td>
            <vs-td>{{ tr.UserEmail || '-' }}</vs-td>
            <vs-td v-if="false">{{ tr.Context || '-' }}</vs-td>
            <vs-td>{{ tr.SopHint || '-' }}</vs-td>
            <vs-td>
              <vs-chip :color="tr.Processed ? 'success' : 'warning'">
                {{ tr.Processed ? $t('Processed') : $t('Unprocessed') }}
              </vs-chip>
            </vs-td>
            <vs-td>
              <div class="text-sm">
                <div>{{ formatDate(tr.CreatedAt)[0] }}</div>
                <div class="text-xs text-gray-500">{{ formatDate(tr.CreatedAt)[1] }}</div>
              </div>
            </vs-td>
            <vs-td>
              <div v-if="tr.Tags && tr.Tags.length > 0">
                <vs-chip 
                  v-for="tag in tr.Tags" 
                  :key="tag.Id"
                  :color="tag.Color"
                  size="small"
                  class="mr-1 mb-1">
                  {{ tag.Name }}
                </vs-chip>
              </div>
              <span v-else class="text-gray-400">-</span>
            </vs-td>
            <vs-td>
              <vs-dropdown vs-trigger-click>
                <vs-button size="small" type="border" icon="more_vert"></vs-button>
                <vs-dropdown-menu>
                  <vs-dropdown-item @click="viewSignalDetails(tr)">
                    {{ $t('ViewDetails') }}
                  </vs-dropdown-item>
                  <vs-dropdown-item @click="editSignalTags(tr)">
                    {{ $t('EditTags') }}
                  </vs-dropdown-item>
                </vs-dropdown-menu>
              </vs-dropdown>
            </vs-td>
          </vs-tr>
          </tbody>
        </template>
      </vs-table>

      <!-- Pagination Controls -->
      <div class="mt-4 flex justify-between items-center">
        <div class="text-sm text-gray-600">
          Showing {{ ((currentPage - 1) * pageSize) + 1 }} to {{ Math.min(currentPage * pageSize, totalCount) }} of {{ totalCount }} signals
        </div>
        
        <div class="flex items-center gap-2">
          <vs-button class="mr-5"
            size="small" 
            type="border" 
            icon="chevron_left"
            :disabled="currentPage <= 1"
            @click="goToPage(currentPage - 1)">
          </vs-button>
          
          <div class="flex items-center gap-1">
            <vs-button 
              v-for="page in visiblePages" 
              :key="page"
              size="small" 
              :type="page === currentPage ? 'filled' : 'border'"
              :color="page === currentPage ? 'primary' : 'default'"
              @click="goToPage(page)">
              {{ page }}
            </vs-button>
          </div>
          
          <vs-button 
            size="small" 
            type="border" 
            icon="chevron_right"
            :disabled="currentPage >= totalPages"
            @click="goToPage(currentPage + 1)">
          </vs-button>
        </div>
        
        <div class="flex items-center gap-2">
          <span class="text-sm text-gray-600 mr-5">Per page:</span>
          <vs-select 
            v-model="pageSize" 
            @input="onPageSizeChange"
            style="width: 80px;">
            <vs-select-item value="10" text="10" />
            <vs-select-item value="20" text="20" />
            <vs-select-item value="50" text="50" />
            <vs-select-item value="100" text="100" />
          </vs-select>
        </div>
      </div>
    </vx-card>

    <!-- Filter Management Modal -->
    <vs-popup :title="$t('ManageFilters')" :active.sync="showFilterModal" class="holamundo">
      <div class="con-exemple-prompt">
        <div class="mb-4">
          <vs-button color="success" type="filled" icon="add" @click="showCreateFilterModal = true">
            {{ $t('CreateNewFilter') }}
          </vs-button>
        </div>
        
        <vs-table :data="filters">
          <template slot="thead">
            <vs-th>{{ $t('Name') }}</vs-th>
            <vs-th>{{ $t('Field') }}</vs-th>
            <vs-th>{{ $t('Operation') }}</vs-th>
            <vs-th>{{ $t('Value') }}</vs-th>
            <vs-th>{{ $t('Actions') }}</vs-th>
          </template>
          <template slot-scope="{ data }">
            <tbody>
              <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
                <vs-td>{{ tr.name }}</vs-td>
                <vs-td>{{ tr.fieldName }}</vs-td>
                <vs-td>{{ tr.operation }}</vs-td>
                <vs-td>{{ tr.value }}</vs-td>
                <vs-td>
                  <vs-button size="small" type="border" color="danger" @click="deleteFilter(tr.id)">
                    {{ $t('Delete') }}
                  </vs-button>
                </vs-td>
              </vs-tr>
            </tbody>
          </template>
        </vs-table>
        
        <!-- Create Filter Modal - Nested inside Filter Management Modal -->
        <vs-popup :title="$t('CreateFilter')" :active.sync="showCreateFilterModal">
          <div class="con-exemple-prompt">
            <vs-input
              v-model="newFilter.name"
              :placeholder="$t('FilterName')"
              :label="$t('FilterName')"
              class="mb-4">
            </vs-input>
            
            <vs-input
              v-model="newFilter.description"
              :placeholder="$t('Description')"
              :label="$t('Description')"
              class="mb-4">
            </vs-input>
            
            <vs-select
              v-model="newFilter.fieldName"
              :placeholder="$t('SelectField')"
              :label="$t('Field')"
              class="mb-4">
              <vs-select-item 
                v-for="(field, index) in filterFields" 
                :key="index"
                :value="field.Name" 
                :text="field.Name" />
            </vs-select>
            
            <vs-select
              v-model="newFilter.operation"
              :placeholder="$t('SelectOperation')"
              :label="$t('Operation')"
              class="mb-4">
              <vs-select-item 
                v-for="op in getOperationsForField(newFilter.fieldName)" 
                :key="op"
                :value="op" 
                :text="op" />
            </vs-select>
            
            <vs-input
              v-model="newFilter.value"
              :placeholder="$t('Value')"
              :label="$t('Value')"
              class="mb-4">
            </vs-input>
            
            <vs-input
              v-if="newFilter.operation === 'between'"
              v-model="newFilter.value2"
              :placeholder="$t('SecondValue')"
              :label="$t('SecondValue')"
              class="mb-4">
            </vs-input>
            
            <div class="flex justify-end">
              <vs-button color="primary" type="filled" @click="createFilter">
                {{ $t('CreateFilter') }}
              </vs-button>
              <vs-button color="danger" type="border" @click="cancelCreateFilter" class="ml-2">
                {{ $t('Cancel') }}
              </vs-button>
            </div>
          </div>
        </vs-popup>
      </div>
    </vs-popup>

    <!-- Tag Management Modal -->
    <vs-popup :title="$t('ManageTags')" :active.sync="showTagModal" class="holamundo">
      <div class="con-exemple-prompt">
        <div class="mb-4">
          <vs-button color="success" type="filled" icon="add" @click="showCreateTagModal = true">
            {{ $t('CreateNewTag') }}
          </vs-button>
        </div>
        
        <vs-table :data="tags">
          <template slot="thead">
            <vs-th>{{ $t('Name') }}</vs-th>
            <vs-th>{{ $t('Color') }}</vs-th>
            <vs-th>{{ $t('Description') }}</vs-th>
            <vs-th>{{ $t('Actions') }}</vs-th>
          </template>
          <template slot-scope="{ data }">
            <tbody>
              <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
                <vs-td>{{ tr.name }}</vs-td>
                <vs-td>
                  <div 
                    :style="{ backgroundColor: tr.color, width: '20px', height: '20px', borderRadius: '50%' }"
                    class="inline-block">
                  </div>
                </vs-td>
                <vs-td>{{ tr.description || '-' }}</vs-td>
                <vs-td>
                  <vs-button size="small" type="border" color="danger" @click="deleteTag(tr.id)">
                    {{ $t('Delete') }}
                  </vs-button>
                </vs-td>
              </vs-tr>
            </tbody>
          </template>
        </vs-table>
        
        <!-- Create Tag Modal - Nested inside Tag Management Modal -->
        <vs-popup :title="$t('CreateTag')" :active.sync="showCreateTagModal">
          <div class="con-exemple-prompt">
            <vs-input
              v-model="newTag.name"
              :placeholder="$t('TagName')"
              :label="$t('TagName')"
              class="mb-4">
            </vs-input>
            
            <vs-input
              v-model="newTag.description"
              :placeholder="$t('Description')"
              :label="$t('Description')"
              class="mb-4">
            </vs-input>
            
            <vs-input
              v-model="newTag.color"
              placeholder="#007bff"
              :label="$t('ColorHex')"
              class="mb-4">
            </vs-input>
            
            <div class="flex justify-end">
              <vs-button color="primary" type="filled" @click="createTag">
                {{ $t('CreateTag') }}
              </vs-button>
              <vs-button color="danger" type="border" @click="cancelCreateTag" class="ml-2">
                {{ $t('Cancel') }}
              </vs-button>
            </div>
          </div>
        </vs-popup>
      </div>
    </vs-popup>

    <!-- Apply Tags Modal -->
    <vs-popup :title="$t('ApplyTagsToSignals')" :active.sync="showApplyTagsModal" class="holamundo">
      <div class="con-exemple-prompt">
        <div class="mb-4">
          <label class="vs-input--label">{{ $t('SelectFilters') }}</label>
          <vs-select
            v-model="applyTagsRequest.filterIds"
            multiple
            :placeholder="$t('SelectFiltersToApply')">
            <vs-select-item 
              v-for="filter in filters" 
              :key="filter.id"
              :value="filter.id" 
              :text="filter.name" />
          </vs-select>
        </div>
        
        <div class="mb-4">
          <label class="vs-input--label">{{ $t('SelectTags') }}</label>
          <vs-select
            v-model="applyTagsRequest.tagIds"
            multiple
            :placeholder="$t('SelectTagsToApply')">
            <vs-select-item 
              v-for="tag in tags" 
              :key="tag.id"
              :value="tag.id" 
              :text="tag.name" />
          </vs-select>
        </div>
        
        <div class="flex justify-end">
          <vs-button color="primary" type="filled" @click="applyTags" :loading="applyingTags">
            {{ $t('ApplyTags') }}
          </vs-button>
        </div>
      </div>
    </vs-popup>

    <!-- Create Test Signal Modal -->
    <vs-popup :title="$t('CreateTestSignal')" :active.sync="showCreateSignalModal" class="holamundo">
      <div class="con-exemple-prompt">
        <vs-input
          v-model="testSignal.system"
          placeholder="Sitefinity"
          :label="$t('System')"
          class="mb-4">
        </vs-input>
        
        <vs-input
          v-model="testSignal.action"
          placeholder="PageOpen"
          :label="$t('Action')"
          class="mb-4">
        </vs-input>
        
        <vs-input
          v-model="testSignal.userEmail"
          placeholder="user@example.com"
          :label="$t('UserEmail')"
          class="mb-4">
        </vs-input>
        
        <vs-input
          v-model="testSignal.context"
          placeholder="/settings/permissions"
          :label="$t('Context')"
          class="mb-4">
        </vs-input>
        
        <vs-input
          v-model="testSignal.sopHint"
          placeholder="sitefinity-settings"
          :label="$t('SOPHint')"
          class="mb-4">
        </vs-input>
        
        <div class="flex justify-end">
          <vs-button color="primary" type="filled" @click="createTestSignal">
            {{ $t('CreateTestSignal') }}
          </vs-button>
        </div>
      </div>
    </vs-popup>

    <!-- Signal Details Modal -->
    <vs-popup fullscreen
      title="Signal Details" 
      :active.sync="showDetailsModal" 
      class="signal-details-modal"
      style="">
      <div class="con-exemple-prompt signal-details-content" v-if="selectedSignal">
        <!-- Signal Information -->
        <div class="mb-6">
          <h4 class="mb-4 text-lg font-semibold">Signal Information</h4>
          <div class="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label class="text-sm font-medium text-gray-600">ID</label>
              <div class="text-base">{{ selectedSignal.Id }}</div>
            </div>
            <div>
              <label class="text-sm font-medium text-gray-600">System</label>
              <div class="text-base">
                <vs-chip color="primary">{{ selectedSignal.System }}</vs-chip>
              </div>
            </div>
            <div>
              <label class="text-sm font-medium text-gray-600">Action</label>
              <div class="text-base">{{ selectedSignal.Action }}</div>
            </div>
            <div>
              <label class="text-sm font-medium text-gray-600">User Email</label>
              <div class="text-base">{{ selectedSignal.UserEmail || '-' }}</div>
            </div>
            <div>
              <label class="text-sm font-medium text-gray-600">SOP Hint</label>
              <div class="text-base">{{ selectedSignal.SopHint || '-' }}</div>
            </div>
            <div>
              <label class="text-sm font-medium text-gray-600">Environment</label>
              <div class="text-base">{{ selectedSignal.Environment || 'prod' }}</div>
            </div>
            <div>
              <label class="text-sm font-medium text-gray-600">Processed</label>
              <div class="text-base">
                <vs-chip :color="selectedSignal.Processed ? 'success' : 'warning'">
                  {{ selectedSignal.Processed ? 'Processed' : 'Unprocessed' }}
                </vs-chip>
              </div>
            </div>
            <div>
              <label class="text-sm font-medium text-gray-600">Created At</label>
              <div class="text-base">{{ formatDate(selectedSignal.CreatedAt)[0] }} {{ formatDate(selectedSignal.CreatedAt)[1] }}</div>
            </div>
            <div v-if="selectedSignal.Tags && selectedSignal.Tags.length > 0" class="col-span-2">
              <label class="text-sm font-medium text-gray-600">Tags</label>
              <div class="text-base">
                <vs-chip 
                  v-for="tag in selectedSignal.Tags" 
                  :key="tag.Id"
                  :color="tag.Color"
                  size="small"
                  class="mr-1 mb-1">
                  {{ tag.Name }}
                </vs-chip>
              </div>
            </div>
          </div>
          
          <!-- Context on its own row -->
          <div class="mb-4">
            <label class="text-sm font-medium text-gray-600">Context</label>
            <div class="text-base break-words">{{ selectedSignal.Context || '-' }}</div>
          </div>
        </div>

        <!-- Metadata Editor -->
        <div class="mb-6">
          <div class="flex justify-between items-center mb-2">
            <h4 class="text-lg font-semibold">Metadata (JSON)</h4>
            <vs-button 
              size="small" 
              type="border" 
              icon="content_copy"
              @click="copyMetadata">
              Copy
            </vs-button>
          </div>
          <div style="border: 1px solid #ddd; border-radius: 4px; overflow: hidden; height: calc(100vh - 400px); min-height: 400px;">
            <MonacoEditor 
              v-if="showDetailsModal && selectedSignal"
              :key="'metadata-' + (selectedSignal ? selectedSignal.Id : 'none')"
              ref="metadataEditor"
              :suffix="'signal-metadata-' + (selectedSignal ? selectedSignal.Id : 'none')"
              :text_value="formattedMetadata || ''"
              language="json"
              theme="vs-light"
              :readonly="true"
              :options="{
                minimap: { enabled: true },
                wordWrap: 'on',
                automaticLayout: true,
                fontSize: 13,
                scrollBeyondLastLine: false,
                readOnly: true
              }">
            </MonacoEditor>
            <div v-if="showDetailsModal && selectedSignal && !formattedMetadata" style="display: flex; align-items: center; justify-content: center; height: 100%; color: #666;">
              <div>No metadata available</div>
            </div>
            <div v-if="!showDetailsModal || !selectedSignal" style="display: flex; align-items: center; justify-content: center; height: 100%; color: #666;">
              <div>Loading editor...</div>
            </div>
          </div>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import TCVelikClient from '@/TCVelikClient/TCVelikClient';
import VxCard from "../../components/vx-card/VxCard.vue";
import MonacoEditor from '@/views/components/extra-components/editors/MonacoEditor.vue';

export default {
  name: 'SignalsView',
  components: {
    VxCard,
    MonacoEditor
  },
  data() {
    return {
      signals: [],
      filters: [],
      tags: [],
      filterFields: [],
      loading: false,
      applyingTags: false,
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
      totalPages: 0,
      
      // Quick filters
      quickFilters: {
        system: '',
        action: '',
        userEmail: '',
        processed: ''
      },
      
      // Modals
      showFilterModal: false,
      showCreateFilterModal: false,
      showTagModal: false,
      showCreateTagModal: false,
      showApplyTagsModal: false,
      showCreateSignalModal: false,
      showDetailsModal: false,
      selectedSignal: null,
      
      // New items
      newFilter: {
        name: '',
        description: '',
        fieldName: '',
        operation: '',
        value: '',
        value2: ''
      },
      
      newTag: {
        name: '',
        description: '',
        color: '#007bff'
      },
      
      applyTagsRequest: {
        filterIds: [],
        tagIds: [],
        appliedBy: 'admin'
      },
      
      testSignal: {
        system: 'Sitefinity',
        action: 'PageOpen',
        userEmail: 'test@example.com',
        context: '/settings/permissions',
        sopHint: 'sitefinity-settings'
      }
    };
  },
  mounted() {
    this.loadSignals();
    this.loadFilters();
    this.loadTags();
    this.loadFilterFields();
  },
  computed: {
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      const start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      const end = Math.min(this.totalPages, start + maxVisible - 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      return pages;
    },
    
    formattedMetadata() {
      if (!this.selectedSignal) {
        return '';
      }
      
      // Handle null, undefined, or empty Metadata
      if (!this.selectedSignal.Metadata || this.selectedSignal.Metadata === null || this.selectedSignal.Metadata === undefined) {
        return '';
      }
      
      try {
        // Convert to string first
        const metadataStr = String(this.selectedSignal.Metadata);
        if (!metadataStr.trim()) {
          return '';
        }
        
        // Try to parse and format as JSON
        const parsed = JSON.parse(metadataStr);
        return JSON.stringify(parsed, null, 2);
      } catch (e) {
        // If not valid JSON, return as-is string
        return String(this.selectedSignal.Metadata);
      }
    }
  },
  watch: {
    showDetailsModal(newVal) {
      if (newVal && this.selectedSignal) {
        // When modal opens, wait for it to render then update Monaco
        this.$nextTick(() => {
          setTimeout(() => {
            if (this.$refs.metadataEditor && this.formattedMetadata) {
              // Trigger editor update if it exists
              this.$forceUpdate();
            }
          }, 500);
        });
      }
    }
  },
  methods: {
    async loadSignals() {
      this.loading = true;
      try {
        const params = new URLSearchParams();
        params.append('page', this.currentPage.toString());
        params.append('pageSize', this.pageSize.toString());
        
        if (this.quickFilters.system) params.append('system', this.quickFilters.system);
        if (this.quickFilters.action) params.append('action', this.quickFilters.action);
        if (this.quickFilters.userEmail) params.append('userEmail', this.quickFilters.userEmail);
        if (this.quickFilters.processed) params.append('processed', this.quickFilters.processed);
        
        const response = await TCVelikClient.GetRelative(`/api/signals/list?${params.toString()}`);
        if (response && response.data) {
          this.signals = response.data.signals || [];
          this.totalCount = response.data.totalCount || 0;
          this.totalPages = response.data.totalPages || 0;
        }
      } catch (error) {
        console.error('Failed to load signals:', error);
        this.$vs.notify({
          title: this.$t('Error'),
          text: this.$t('FailedToLoadSignals'),
          color: 'danger',
          position: 'top-right'
        });
      } finally {
        this.loading = false;
      }
    },
    
    async loadFilters() {
      try {
        const response = await TCVelikClient.GetRelative('/api/signals/filters');
        if (response && response.data) {
          this.filters = response.data;
        }
      } catch (error) {
        console.error('Failed to load filters:', error);
      }
    },
    
    async loadTags() {
      try {
        const response = await TCVelikClient.GetRelative('/api/signals/tags');
        if (response && response.data) {
          this.tags = response.data;
        }
      } catch (error) {
        console.error('Failed to load tags:', error);
      }
    },
    
    async loadFilterFields() {
      try {
        const response = await TCVelikClient.GetRelative('/api/signals/filter-fields');
        if (response && response.data) {
          this.filterFields = response.data;
        }
      } catch (error) {
        console.error('Failed to load filter fields:', error);
      }
    },
    
    applyQuickFilters() {
      this.currentPage = 1;
      this.loadSignals();
    },
    
    getOperationsForField(fieldName) {
      const field = this.filterFields.find(f => f.Name === fieldName);
      return field ? field.Operations : [];
    },
    
    async createFilter() {
      try {
        const response = await TCVelikClient.PostRelative('/api/signals/filters', this.newFilter);
        if (response && response.data) {
          this.$vs.notify({
            title: this.$t('Success'),
            text: this.$t('FilterCreatedSuccessfully'),
            color: 'success',
            position: 'top-right'
          });
          this.showCreateFilterModal = false;
          this.newFilter = {
            name: '',
            description: '',
            fieldName: '',
            operation: '',
            value: '',
            value2: ''
          };
          this.loadFilters();
        }
      } catch (error) {
        console.error('Failed to create filter:', error);
        this.$vs.notify({
          title: this.$t('Error'),
          text: this.$t('FailedToCreateFilter'),
          color: 'danger',
          position: 'top-right'
        });
      }
    },
    
    cancelCreateFilter() {
      this.showCreateFilterModal = false;
      this.newFilter = {
        name: '',
        description: '',
        fieldName: '',
        operation: '',
        value: '',
        value2: ''
      };
    },
    
    async createTag() {
      try {
        const response = await TCVelikClient.PostRelative('/api/signals/tags', this.newTag);
        if (response && response.data) {
          this.$vs.notify({
            title: this.$t('Success'),
            text: this.$t('TagCreatedSuccessfully'),
            color: 'success',
            position: 'top-right'
          });
          this.showCreateTagModal = false;
          this.newTag = {
            name: '',
            description: '',
            color: '#007bff'
          };
          this.loadTags();
        }
      } catch (error) {
        console.error('Failed to create tag:', error);
        this.$vs.notify({
          title: this.$t('Error'),
          text: this.$t('FailedToCreateTag'),
          color: 'danger',
          position: 'top-right'
        });
      }
    },
    
    cancelCreateTag() {
      this.showCreateTagModal = false;
      this.newTag = {
        name: '',
        description: '',
        color: '#007bff'
      };
    },
    
    async applyTags() {
      this.applyingTags = true;
      try {
        const response = await TCVelikClient.PostRelative('/api/signals/apply-tags', this.applyTagsRequest);
        if (response && response.data) {
          this.$vs.notify({
            title: this.$t('Success'),
            text: response.data.message,
            color: 'success',
            position: 'top-right'
          });
          this.showApplyTagsModal = false;
          this.applyTagsRequest = {
            filterIds: [],
            tagIds: [],
            appliedBy: 'admin'
          };
          this.loadSignals();
        }
      } catch (error) {
        console.error('Failed to apply tags:', error);
        this.$vs.notify({
          title: this.$t('Error'),
          text: this.$t('FailedToApplyTags'),
          color: 'danger',
          position: 'top-right'
        });
      } finally {
        this.applyingTags = false;
      }
    },
    
    async createTestSignal() {
      try {
        const response = await TCVelikClient.PostRelative('/api/signals/ingest', this.testSignal);
        if (response && response.data) {
          this.$vs.notify({
            title: this.$t('Success'),
            text: this.$t('TestSignalCreatedSuccessfully'),
            color: 'success',
            position: 'top-right'
          });
          this.showCreateSignalModal = false;
          this.loadSignals();
        }
      } catch (error) {
        console.error('Failed to create test signal:', error);
        this.$vs.notify({
          title: this.$t('Error'),
          text: this.$t('FailedToCreateTestSignal'),
          color: 'danger',
          position: 'top-right'
        });
      }
    },
    
    async viewSignalDetails(signal) {
      // Load full signal details including Metadata and Context
      try {
        const response = await TCVelikClient.GetRelative(`/api/signals/${signal.Id}`);
        if (response && response.data) {
          this.selectedSignal = response.data;
          
          // Debug logging
          console.log('Signal details loaded:', {
            id: this.selectedSignal.Id,
            hasMetadata: !!this.selectedSignal.Metadata,
            metadataType: typeof this.selectedSignal.Metadata,
            metadataLength: this.selectedSignal.Metadata ? String(this.selectedSignal.Metadata).length : 0,
            formattedMetadataLength: this.formattedMetadata ? this.formattedMetadata.length : 0
          });
          
          // Set modal to show - this triggers the modal to render
          this.showDetailsModal = true;
          
          // Wait for modal DOM to be ready
          await this.$nextTick();
          
          // Give extra time for modal animation to complete (fullscreen modal takes longer)
          await new Promise(resolve => setTimeout(resolve, 600));
          
          // Ensure Monaco editor component is mounted and try to trigger update
          await this.$nextTick();
          
          // If Monaco editor ref exists, ensure it has the latest data
          if (this.$refs.metadataEditor) {
            if (this.$refs.metadataEditor.editorInstance) {
              const currentValue = this.$refs.metadataEditor.getValue();
              const newValue = this.formattedMetadata;
              if (currentValue !== newValue) {
                const model = this.$refs.metadataEditor.editorInstance.getModel();
                if (model) {
                  model.setValue(newValue || '');
                }
              }
            } else {
              // Editor not initialized yet, wait a bit more
              console.log('Monaco editor not initialized yet, waiting...');
              await new Promise(resolve => setTimeout(resolve, 500));
              await this.$nextTick();
              
              // Try again
              if (this.$refs.metadataEditor && this.$refs.metadataEditor.editorInstance) {
                const model = this.$refs.metadataEditor.editorInstance.getModel();
                if (model && this.formattedMetadata) {
                  model.setValue(this.formattedMetadata);
                }
              }
            }
          }
        } else {
          this.$vs.notify({
            title: 'Error',
            text: 'Failed to load signal details',
            color: 'danger',
            position: 'top-right'
          });
        }
      } catch (error) {
        console.error('Failed to load signal details:', error);
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to load signal details',
          color: 'danger',
          position: 'top-right'
        });
      }
    },
    
    copyMetadata() {
      const metadata = this.formattedMetadata;
      if (metadata) {
        this.$copyText(metadata).then(() => {
          this.$vs.notify({
            title: 'Success',
            text: 'Metadata copied to clipboard',
            color: 'success',
            position: 'top-right'
          });
        }).catch(() => {
          this.$vs.notify({
            title: 'Error',
            text: 'Failed to copy metadata',
            color: 'danger',
            position: 'top-right'
          });
        });
      }
    },
    
    editSignalTags(signal) {
      // TODO: Implement signal tag editing
      console.log('Edit signal tags:', signal);
    },
    
    deleteFilter(filterId) {
      // TODO: Implement filter deletion
      console.log('Delete filter:', filterId);
    },
    
    deleteTag(tagId) {
      // TODO: Implement tag deletion
      console.log('Delete tag:', tagId);
    },
    
    formatDate(dateString) {
      if (!dateString) return ['-', '-'];
      
      const date = new Date(dateString);
      const dateStr = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      const timeStr = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
      
      return [dateStr, timeStr];
    },
    
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.loadSignals();
      }
    },
    
    onPageSizeChange() {
      this.currentPage = 1;
      this.loadSignals();
    }
  }
};
</script>

<style scoped>
.con-exemple-prompt {
  padding: 10px;
}

.grid {
  display: grid;
}

.grid-cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.col-span-2 {
  grid-column: span 2;
}

.gap-4 {
  gap: 1rem;
}

.mb-6 {
  margin-bottom: 1.5rem;
}

.break-words {
  word-break: break-word;
  overflow-wrap: break-word;
}
</style>
